import { useMemo, useState } from 'react'
import { MousePointerClick, Layers } from 'lucide-react'
import { Odontogram, ADULT_QUADRANTS, PEDO_QUADRANTS } from './ToothChart/Odontogram.jsx'
import { classifyTooth } from './ToothChart/data.js'
import { typeMeta } from '../lib/oral-exam.js'

const FULL_MOUTH = 'Full Mouth'

/**
 * Read-only oral-exam visualization: a marked odontogram (each affected tooth
 * shows its finding abbreviation — DC / Ext / RCT …), a legend, a click-to-view
 * tooth panel, and a Full Mouth list. Shared by the chart Summary's Oral Exam
 * view and the per-visit Oral Exam card in the History detail page.
 *
 * Props:
 *   findings: Array<{ tag: string, type: string, detail: string }>
 *             tag = single FDI tooth number or 'Full Mouth'.
 *   framed:   when true (default, standalone use in the Summary) the inner
 *             blocks are white bordered cards. When false (embedded inside a
 *             card, e.g. the History Oral Exam section) they become borderless
 *             tinted blocks so we don't stack a border inside a border.
 */
export default function OralExamChart({ findings, framed = true }) {
  const [selected, setSelected] = useState(null)

  const { byTooth, fullMouth, hasPedo, typesPresent } = useMemo(() => {
    const byTooth = new Map()
    const fullMouth = []
    for (const e of findings || []) {
      const tag = normalizeTag(e?.tag)
      const f = { tag, type: e?.type || '', detail: e?.detail || '' }
      if (!tag || tag === FULL_MOUTH) { fullMouth.push(f); continue }
      if (!byTooth.has(tag)) byTooth.set(tag, [])
      byTooth.get(tag).push(f)
    }
    const hasPedo = [...byTooth.keys()].some((n) => classifyTooth(n) === 'pedo')
    const seen = new Set()
    for (const arr of byTooth.values()) for (const f of arr) if (f.type) seen.add(f.type)
    for (const f of fullMouth) if (f.type) seen.add(f.type)
    return { byTooth, fullMouth, hasPedo, typesPresent: [...seen] }
  }, [findings])

  if (byTooth.size === 0 && fullMouth.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 px-4 py-10 text-center text-[14px] text-slate-500">
        ยังไม่มีข้อมูล Oral Exam ในเคสนี้
      </div>
    )
  }

  const selectedFindings = selected ? (byTooth.get(selected) || []) : null
  const makeCell = (tone) => (num) => {
    const fs = byTooth.get(num) || []
    return (
      <MarkToothCell
        key={num}
        num={num}
        findings={fs}
        selected={selected === num}
        onSelect={(n) => setSelected((cur) => (cur === n ? null : n))}
        tone={tone}
      />
    )
  }

  const chartBox = framed
    ? 'bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 overflow-x-auto'
    : 'bg-slate-50/50 rounded-xl p-4 overflow-x-auto'
  const listBox = framed
    ? 'bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden'
    : 'bg-slate-50/50 rounded-xl divide-y divide-slate-100 overflow-hidden'

  return (
    <div className="space-y-4">
      <div className={chartBox}>
        <Odontogram quadrants={ADULT_QUADRANTS} label="ฟันแท้ (Adult)" renderCell={makeCell('blue')} />
        {hasPedo && (
          <div className="mt-6 pt-5 border-t border-dashed border-slate-200">
            <Odontogram quadrants={PEDO_QUADRANTS} label="ฟันน้ำนม (Pedo)" renderCell={makeCell('violet')} />
          </div>
        )}
        {typesPresent.length > 0 && <Legend types={typesPresent} />}
      </div>

      {selected ? (
        <ToothFindingPanel tooth={selected} findings={selectedFindings} framed={framed} onClose={() => setSelected(null)} />
      ) : (
        byTooth.size > 0 && (
          <div className="flex items-center justify-center gap-1.5 text-[13.5px] text-slate-400 py-1">
            <MousePointerClick size={13} /> คลิกฟันที่มีเครื่องหมายเพื่อดู finding
          </div>
        )
      )}

      {fullMouth.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2 px-0.5">
            <Layers size={13} className="text-amber-500" strokeWidth={2} />
            <span className="text-[13.5px] uppercase tracking-[0.12em] font-semibold text-slate-500">Full Mouth · ทั้งปาก</span>
            <span className="text-[12.5px] text-slate-400">{fullMouth.length}</span>
          </div>
          <div className={listBox}>
            {fullMouth.map((f, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center gap-2.5 flex-wrap">
                {f.type && <TypeBadge type={f.type} />}
                <span className="text-[14px] text-slate-700">{f.detail || <span className="text-slate-400 italic">(ไม่ระบุ)</span>}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                      */
/* -------------------------------------------------------------------------- */

const MARK_TONE = {
  blue:   { sel: 'ring-2 ring-slate-900' },
  violet: { sel: 'ring-2 ring-slate-900' },
}

function MarkToothCell({ num, findings, selected, onSelect }) {
  const has = findings.length > 0
  const primary = has ? typeMeta(findings[0].type) : null
  const more = findings.length - 1
  return (
    <button
      type="button"
      disabled={!has}
      onClick={() => has && onSelect(num)}
      title={has ? `ฟัน ${num} · ${findings.map((f) => f.type).filter(Boolean).join(', ')}` : `ฟัน ${num} · ไม่มี finding`}
      aria-pressed={selected}
      className={`relative flex flex-col items-center justify-between w-[40px] h-[46px] rounded-md ring-1 py-1 transition-all select-none ${
        has ? 'cursor-pointer hover:-translate-y-[1px]' : 'cursor-default'
      } ${
        selected
          ? `${primary ? primary.bg : 'bg-white'} ${MARK_TONE.blue.sel}`
          : has
            ? `${primary.bg} ${primary.ring}`
            : 'bg-white ring-slate-200'
      }`}
    >
      <span className={`text-[11px] font-mono leading-none ${has ? 'text-slate-500' : 'text-slate-300'}`}>{num}</span>
      {has ? (
        <span className={`font-bold leading-none ${primary.text} ${primary.abbr.length >= 4 ? 'text-[9.5px]' : 'text-[12.5px]'}`}>
          {primary.abbr}
          {more > 0 && <sup className="text-[9.5px] font-semibold">+{more}</sup>}
        </span>
      ) : (
        <span className="w-4 h-px bg-slate-100" />
      )}
    </button>
  )
}

function Legend({ types }) {
  return (
    <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-1.5">
      {types.map((t) => {
        const m = typeMeta(t)
        return (
          <span key={t} className="inline-flex items-center gap-1.5 text-[13px] text-slate-500">
            <span className={`inline-flex items-center justify-center min-w-[24px] h-[18px] px-1 rounded text-[11.5px] font-bold ${m.bg} ${m.text} ring-1 ${m.ring}`}>{m.abbr}</span>
            {t}
          </span>
        )
      })}
    </div>
  )
}

function ToothFindingPanel({ tooth, findings, framed = true, onClose }) {
  const kind = classifyTooth(tooth) === 'pedo' ? 'ฟันน้ำนม' : 'ฟันแท้'
  return (
    <section className={`overflow-hidden ${framed ? 'bg-white border border-slate-200 rounded-2xl' : 'bg-slate-50/60 rounded-xl'}`}>
      <header className="px-5 pt-4 pb-3.5 flex items-center gap-3 border-b border-slate-100">
        <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center flex-none ring-1 ring-brand-100 font-mono text-[16px] font-semibold">{tooth}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[16px] font-semibold text-slate-900 tracking-tight">ฟันซี่ {tooth}</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">{kind} · {findings.length} finding</div>
        </div>
        <button type="button" onClick={onClose} className="text-[13.5px] text-slate-500 hover:text-brand-700 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">ล้างที่เลือก</button>
      </header>
      <div className="divide-y divide-slate-100">
        {findings.map((f, i) => (
          <div key={i} className="px-5 py-3 flex items-center gap-2.5 flex-wrap">
            <TypeBadge type={f.type} />
            {f.detail && <span className="text-[14px] text-slate-600">{f.detail}</span>}
          </div>
        ))}
      </div>
    </section>
  )
}

function TypeBadge({ type }) {
  const m = typeMeta(type)
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[13px] font-semibold ring-1 ${m.bg} ${m.text} ${m.ring}`}>
      <span className={`inline-flex items-center justify-center min-w-[22px] h-[15px] px-0.5 rounded text-[10.5px] font-bold text-white ${m.mark}`}>{m.abbr}</span>
      {type || '—'}
    </span>
  )
}

function normalizeTag(t) {
  if (Array.isArray(t)) return String(t[0] || '')
  return String(t || '')
}
